import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { API } from '../../API';
import { Loader } from '../../components/Loader/Loader';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { WindmillApp } from '../WindmillAppPage/types';

import styles from './WindmillAppsPage.module.scss';

export function WindmillAppsPage() {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const [apps, setApps] = useState<WindmillApp[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isSuperAdmin = currentUser?.is_super_admin ?? false;

  const fetchApps = useCallback(() => {
    API.fetch('GET', '/API/v2/windmill/apps')
      .then((data: WindmillApp[]) => {
        setApps(data);
        setError(null);
      })
      .catch((err: Error) => {
        setApps([]);
        setError(err.message);
      });
  }, []);

  useEffect(fetchApps, [fetchApps]);

  if (!apps && !error) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className={styles.root}>
        <div className={styles.errorMessage}>{error}</div>
      </div>
    );
  }

  if (!apps || apps.length === 0) {
    return (
      <div className={styles.root}>
        <div className={styles.header}>
          <h1 className={styles.title}>{t('Windmill Apps')}</h1>
          {isSuperAdmin && (
            <Link to="/windmill-admin" className={styles.adminButton}>
              {t('Manage apps')}
            </Link>
          )}
        </div>
        <div className={styles.emptyState}>{t('No apps available')}</div>
      </div>
    );
  }

  // Group by category
  const grouped: Record<string, WindmillApp[]> = {};
  const ungrouped: WindmillApp[] = [];

  for (const app of apps) {
    if (app.category) {
      if (!grouped[app.category]) grouped[app.category] = [];
      grouped[app.category].push(app);
    } else {
      ungrouped.push(app);
    }
  }

  const hasCategories = Object.keys(grouped).length > 0;

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t('Windmill Apps')}</h1>
        {isSuperAdmin && (
          <Link to="/windmill-admin" className={styles.adminButton}>
            {t('Manage apps')}
          </Link>
        )}
      </div>

      {hasCategories ? (
        <>
          {Object.entries(grouped).map(([category, categoryApps]) => (
            <div key={category} className={styles.categorySection}>
              <h2 className={styles.categoryTitle}>{category}</h2>
              <div className={styles.grid}>
                {categoryApps.map((app) => (
                  <AppCard key={app.id} app={app} />
                ))}
              </div>
            </div>
          ))}
          {ungrouped.length > 0 && (
            <div className={styles.categorySection}>
              <h2 className={styles.categoryTitle}>{t('Other')}</h2>
              <div className={styles.grid}>
                {ungrouped.map((app) => (
                  <AppCard key={app.id} app={app} />
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className={styles.grid}>
          {apps.map((app) => (
            <AppCard key={app.id} app={app} />
          ))}
        </div>
      )}
    </div>
  );
}

interface AppCardProps {
  app: WindmillApp;
}

function AppCard({ app }: AppCardProps) {
  const { t } = useTranslation();

  return (
    <Link to={`/windmill-apps/${app.id}`} className={styles.card}>
      <div className={styles.cardHeader}>
        {app.icon && <span className={styles.cardIcon}>{app.icon}</span>}
        <h3 className={styles.cardName}>{app.name}</h3>
      </div>
      {app.description && (
        <p className={styles.cardDescription}>{app.description}</p>
      )}
      <div className={styles.cardFooter}>
        {app.category && (
          <span className={styles.categoryBadge}>{app.category}</span>
        )}
        {app.default_execution_mode === 'async' && (
          <span className={styles.asyncBadge}>{t('async')}</span>
        )}
      </div>
    </Link>
  );
}